import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import LoadingSpinner from '../partials/LoadingSpinner';
import EditAssignmentModal from './EditAssignmentModal';
import DeleteAssignmentModal from './DeleteAssignmentModal';

import { H4, Panel, Box, Text, Button, Badge } from '@bigcommerce/big-design';
import { DeleteIcon, EditIcon, ReceiptIcon, AssignmentIcon, ChevronRightIcon, ChevronLeftIcon } from '@bigcommerce/big-design-icons';

import { assignmentsIndex } from '../../redux/actions/assignments';

const ViewSupplierAssignments = ({ payload, activeSupplier, assignments, getAssignmentsPage, continuationToken, bigcommerceSettings, settings }) => {
  const [submitted, setSubmitted] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [showEditAssignmentModal, setShowEditAssignmentModal] = useState(false);
  const [showDeleteAssignmentModal, setShowDeleteAssignmentModal] = useState(false);

  const [page, setPage] = useState(0);

  useEffect(() => {
    getNextAssignmentPage();
  }, []);

  const showEditModal = (assignment) => {
    setActiveAssignment(assignment);
    setShowEditAssignmentModal(true);
  }

  const showDeleteModal = (assignment) => {
    setActiveAssignment(assignment);
    setShowDeleteAssignmentModal(true);
  }

  const format = (value) => {
    let formattedValue = "";

    if(bigcommerceSettings.currency_symbol_location === 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }
    
    formattedValue += parseFloat(value).toFixed(bigcommerceSettings.decimal_places)

    if(bigcommerceSettings.currency_symbol_location !== 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }

    formattedValue = formattedValue.replace('.', bigcommerceSettings.decimal_separator)

    return formattedValue;
  }

  const getPreviousAssignmentsPage = () => {
    let previousContinuationToken = assignments.assignmentsSearchStack[page - 2];
    getAssignmentsPage(payload, activeSupplier.id, previousContinuationToken, (page - 1));
    setPage(page - 1);
  }

  const getNextAssignmentPage = () => {
    getAssignmentsPage(payload, activeSupplier.id, assignments.assignmentsSearchStack[page], (page + 1));
    setPage(page + 1);
  }

  return (
    <>
      <Panel>
        <div className="full-width">
          <Box marginBottom="medium">
     
            <div className="flex-container">
              <div className="flex-1">
                <Button
                  onClick={() => getPreviousAssignmentsPage()}
                  disabled={page === 1}
                  variant="secondary"
                  iconLeft={<ChevronLeftIcon />}
                >
                  Previous Assignments
                </Button>

                <Button
                  onClick={() => getNextAssignmentPage()}
                  disabled={assignments.lastPage}
                  variant="secondary"
                  iconRight={<ChevronRightIcon />}
                >
                  Next Assignments
                </Button>
              </div>
            </div>
            
            <br/>

            { assignments.isFetching &&
              <LoadingSpinner statusText="Loading Assignments" />
            }

            { assignments.assignments.length === 0 && !assignments.isFetching &&
              <Box
                backgroundColor="warning10"
                border="box"
                borderRadius="normal"
                padding="medium"
              >
                <div className="text-center">
                  <Text>No assignments created for this supplier. Add supplier assignments to see them listed here.</Text>
                </div>
              </Box>
            }

            { assignments.assignments.length > 0 &&
              <div className="zebra-stripe-children">
                { assignments.assignments.map(assignment =>
                  <lineitem>
                    <div className="has-hover sku-line">
                      <H4>{ assignment.variant_id ? 'Variant' : 'Product'} - { assignment?.product_data?.name || assignments.relatedProducts[assignment.product_id]?.name }</H4>
                      <div className="flex-container">
                        <div className="left-15">
                          { assignment?.product_data?.primary_image && <img src={assignment?.product_data?.primary_image.url_tiny} /> }
                          { !assignment?.product_data?.primary_image && assignment?.variant_data?.imageUrl && <img src={assignment?.variant_data?.imageUrl} /> }
                          { !assignment?.variant_data?.imageUrl && !assignment?.product_data?.primary_image && assignments.relatedProducts[assignment.product_id]?.primary_image && <img src={assignments.relatedProducts[assignment.product_id]?.primary_image.url_tiny} /> }
                          { !assignment?.product_data?.primary_image && !assignment?.variant_data?.imageUrl && !assignments.relatedProducts[assignment.product_id]?.primary_image && <Text>No image</Text>}
                        </div>
                        <div className="flex-2">
                          <div className="left-15">
                            <H4>Assignment Details</H4>
                            <ul>
                              <li>Supplier SKU: <Badge label={assignment.supplier_sku || "Not set"} variant={assignment.supplier_sku ? "success" : "warning"} /></li>
                              <li>Supplier Price: <Badge label={assignment.supplier_price ? format(assignment.supplier_price) : "Not set"} variant={assignment.supplier_price ? "success" : "warning"} /></li>
                              <li>Minimum Order Quantity: <Badge label={assignment.minimum_order || "Not set"} variant={assignment.minimum_order ? "success" : "warning"} /></li>
                              { settings?.settings?.order_multiples && 
                                <li>Order Multiple: <Badge label={assignment.order_multiple || "Not set"} variant={assignment.order_multiple ? "success" : "warning"} /></li>
                              }
                              { settings?.settings?.pack_quantities && 
                                <li>Pack Quantity: <Badge label={assignment.pack_quantity || "Not set"} variant={assignment.pack_quantity ? "success" : "warning"} /></li>
                              }
                            </ul>
                          </div>
                        </div>


                        <div className="flex-2">
                          <H4>Item Details</H4>
                          <ul>
                            { assignment?.variant_data?.option_values && assignment?.variant_data?.option_values.map(option =>
                                <li>
                                  <strong>{ option.option_display_name }</strong>: <Badge label={ option.label } variant="secondary" />
                                </li>
                              )
                            }
                            <li>
                              Default Location Stock:
                              <Badge label={ assignment?.product_data?.inventory_level.toString() || assignment?.variant_data?.inventory_level.toString() } variant="primary" />
                              <Badge label={ (assignment?.product_data?.inventory_level === null && assignment?.variant_data?.inventory_level === null) ? "Not Tracked" : "" } variant="warning" />
                            </li>
                          </ul>
                        </div>
                        <div className="flex-1">
                          <div className="left-15">
                            <Button
                              onClick={() => showEditModal(assignment)}
                              variant="secondary"
                              disabled={assignments.updatingAssignments.find(assignmentId => assignmentId === `${assignment.product_id}/${assignment.variant_id}`)}
                              isLoading={assignments.updatingAssignments.find(assignmentId => assignmentId === `${assignment.product_id}/${assignment.variant_id}`)}
                              iconLeft={<EditIcon />}
                            >
                              Edit Assignment
                            </Button>
                            <br/>
                            <br/>
                            <Button
                              onClick={() => showDeleteModal(assignment)}
                              variant="secondary"
                              actionType="destructive"
                              disabled={assignments.updatingAssignments.find(assignmentId => assignmentId === `${assignment.product_id}/${assignment.variant_id}`)}
                              isLoading={assignments.updatingAssignments.find(assignmentId => assignmentId === `${assignment.product_id}/${assignment.variant_id}`)}
                              iconLeft={<DeleteIcon />}
                            >
                              Remove Assignment
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </lineitem>
                )}
              </div>
            }
          </Box>
        </div>
      </Panel>
      <EditAssignmentModal
        payload={payload}
        supplierId={activeSupplier.id}
        showEditAssignmentModal={showEditAssignmentModal}
        setShowEditAssignmentModal={setShowEditAssignmentModal}
        assignment={activeAssignment}
        assignmentName={`${activeAssignment?.variant_id ? 'Variant' : 'Product'} - ${ activeAssignment?.product_data?.name || assignments.relatedProducts[activeAssignment?.product_id]?.name}`}
      />
      <DeleteAssignmentModal
        payload={payload}
        supplierId={activeSupplier.id}
        showDeleteAssignmentModal={showDeleteAssignmentModal}
        setShowDeleteAssignmentModal={setShowDeleteAssignmentModal}
        assignment={activeAssignment}
        assignmentName={`${activeAssignment?.variant_id ? 'Variant' : 'Product'} - ${ activeAssignment?.product_data?.name || assignments.relatedProducts[activeAssignment?.product_id]?.name}`}
      />
    </>
  )

}

const mapStateToProps = state => ({
  assignments: state.assignments,
  continuationToken: state.assignments.assignments?.continuationToken,
  bigcommerceSettings: state.stores.settings,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  getAssignmentsPage: (payload, supplierId, continuationToken, page) => dispatch(assignmentsIndex(payload, supplierId, continuationToken, page)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewSupplierAssignments);
